import { APIClient } from "../../helpers/api_helper";

const api = new APIClient();

const ROLES_ENDPOINT = "/api/v1/admin/rbac/roles";

export interface CreateRolePayload {
  code: string;
  name: string;
  is_system: boolean;
  /** ID of the user creating the role */
  created_by: string;
  /** List of allowed scope types for this role (e.g. ["academy"]) */
  allowed_scope_types: string[];
  /** Permission codes assigned to this role (e.g. ["academy.manage"]) */
  permissions: string[];
}

export interface PermissionSummary {
  _id: string;
  action: string;
  code: string;
  createdAt: string;
  description: string;
  resource: string;
}

export interface RoleResponse {
  _id: string;
  code: string;
  name: string;
  isSystem: boolean;
  createdAt: string;
  /** Legacy single scope type (may be absent in new API) */
  scopeType?: string;
  /** New RBAC API field: list of allowed scope types (e.g. ["academy", "tournament"]) */
  allowedScopeTypes?: string[];
  description?: string;
  permissionsCount?: number;
  createdBy?: string;
  /** Full permission objects from RBAC service */
  permissions?: PermissionSummary[];
  /** Convenience field when API returns only codes */
  permissionCodes?: string[];
}

export interface CreateRoleResponse {
  data: RoleResponse;
}

export interface UpdateRolePayload {
  name?: string;
  description?: string;
  allowed_scope_types?: string[];
  permissions?: string[];
  is_system?: boolean;
}

export interface ListRolesMeta {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface ListRolesResponse {
  data: RoleResponse[];
  meta: ListRolesMeta;
}

export interface ListRolesParams {
  search?: string;
  createdBy?: string;
  scopeType?: string;
  page?: number;
  limit?: number;
}

/**
 * List roles from the API with search, filters and pagination.
 * Uses query params: search, createdBy, scopeType, page, limit.
 */
export const listRoles = async (
  params: ListRolesParams
): Promise<ListRolesResponse> => {
  const {
    search,
    createdBy,
    scopeType,
    page = 1,
    limit = 20,
  } = params;

  // Always send pagination; only include filters that are actually applied
  const queryParams: Record<string, string | number> = {
    page,
    limit,
  };

  if (search && search.trim()) {
    queryParams.search = search.trim();
  }
  if (createdBy && createdBy.trim()) {
    queryParams.createdBy = createdBy.trim();
  }
  if (scopeType && scopeType.trim()) {
    queryParams.scopeType = scopeType.trim();
  }

  const queryString =
    "?" +
    Object.entries(queryParams)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      )
      .join("&");

  const response = await api.get(`${ROLES_ENDPOINT}${queryString}`, undefined);

  if (response && typeof response === "object" && "data" in response && "meta" in response) {
    const data = response as ListRolesResponse;
    return {
      data: Array.isArray(data.data) ? data.data : [],
      meta: data.meta,
    };
  }

  // Fallbacks for older shapes, if backend doesn't fully support filters yet
  if (Array.isArray(response)) {
    const items = response as RoleResponse[];
    return {
      data: items,
      meta: {
        total: items.length,
        page: 1,
        limit: items.length,
        pages: 1,
      },
    };
  }

  return {
    data: [],
    meta: {
      total: 0,
      page: 1,
      limit: limit,
      pages: 0,
    },
  };
};

/**
 * Fetch a single role by its code.
 * Uses endpoint: /api/v1/admin/rbac/roles/{code}
 */
export const getRole = async (code: string): Promise<RoleResponse> => {
  const response = await api.get(`${ROLES_ENDPOINT}/${code}`, undefined);

  if (response && typeof response === "object" && "data" in response) {
    const data = response as { data: RoleResponse };
    return data.data;
  }

  throw new Error("Invalid response from get role API");
};

/**
 * Creates a new role.
 */
export const createRole = async (
  payload: CreateRolePayload
): Promise<RoleResponse> => {
  const response = await api.create(ROLES_ENDPOINT, payload);

  // New RBAC response shape: { data: { ...role... } }
  if (response && typeof response === "object" && "data" in response) {
    const data = response as CreateRoleResponse;
    return data.data;
  }

  throw new Error("Invalid response from create role API");
};

/**
 * Updates an existing role by code.
 * Uses endpoint: /api/v1/admin/rbac/roles/{code}
 */
export const updateRole = async (
  code: string,
  payload: UpdateRolePayload
): Promise<RoleResponse> => {
  const response = await api.put(`${ROLES_ENDPOINT}/${code}`, payload);

  if (response && typeof response === "object" && "data" in response) {
    const data = response as CreateRoleResponse;
    return data.data;
  }

  throw new Error("Invalid response from update role API");
};
